import { Component, EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { v4 as uuidv4 } from 'uuid'; // Import UUID library

@Component({
  selector: 'app-create-customer-dialog',
  templateUrl: './create-customer-dialog.component.html',
  styleUrls: ['./create-customer-dialog.component.scss'],
})
export class CreateCustomerDialogComponent {
  @Output() customerCreated = new EventEmitter<any>();
  @Output() customerUpdated = new EventEmitter<any>();

  display: boolean = false;
  customerForm: FormGroup;
  tempShippingForm: FormGroup;
  isAdding: boolean = false;
  isEditing: boolean = false;
  editingIndex: number | null = null;
  selectedShippingIndex: number | null = null;
  statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Pending', value: 'pending' },
  ];
  isEditMode: boolean = false;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService
  ) {
    this.customerForm = this.fb.group({
      uuid: [this.generateShortUUID()], // Initialize with a new UUID
      customerDetails: this.fb.group({
        customerName: ['', Validators.required],
        customerAddress: ['', Validators.required],
        customerFax: ['', Validators.required],
      }),
      contactPerson: this.fb.group({
        contactPersonName: ['', Validators.required],
        contactPersonEmail: ['', [Validators.required, Validators.email]],
      }),
      shippingDetails: this.fb.array([]),
      status: ['', Validators.required],
    });

    this.tempShippingForm = this.fb.group({
      shippingID: [this.generateShortUUID()], // Initialize with a new shippingID
      companyName: ['', Validators.required],
      shippingAddress: ['', Validators.required],
      street: ['', Validators.required],
      postalCode: ['', Validators.required],
      country: ['', Validators.required],
      fax: ['', Validators.required],
      isSelectedShipping: [false],
    });
  }

  // Helper method to generate a 16-character UUID
  private generateShortUUID(): string {
    const uuid = uuidv4().replace(/-/g, ''); // Remove hyphens
    return uuid.substring(0, 16); // Truncate to 16 characters
  }

  get shippingDetails(): FormArray<FormGroup> {
    return this.customerForm.get('shippingDetails') as FormArray<FormGroup>;
  }

  showDialog(customer?: any): void {
    this.display = true;
    if (customer) {
      this.isEditMode = true;
      this.populateForm(customer);
    } else {
      this.isEditMode = false;
      this.resetForm();
    }
  }

  resetForm(): void {
    this.customerForm.reset({
      uuid: this.generateShortUUID(), // Set a new UUID
    });
    this.shippingDetails.clear();
    this.tempShippingForm.reset({
      shippingID: this.generateShortUUID(), // Reset with a new shippingID
    });
    this.isAdding = false;
    this.isEditing = false;
    this.editingIndex = null;
    this.selectedShippingIndex = null;
  }

  populateForm(customer: any): void {
    console.log('Customer data:', JSON.stringify(customer, null, 2));
    this.resetForm();
    this.customerForm.patchValue({
      uuid: customer.uuid || this.generateShortUUID(),
      customerDetails: customer.customerDetails,
      contactPerson: {
        contactPersonName: customer.contactPerson.contactPersonName,
        contactPersonEmail: customer.contactPerson.contactPersonEmail,
      },
      status: customer.status,
    });

    const shippingDetails = Array.isArray(customer.shippingDetails) ? customer.shippingDetails : [];
    shippingDetails.forEach((shipping: any, index: number) => {
      this.shippingDetails.push(this.fb.group({
        shippingID: [shipping.shippingID || this.generateShortUUID()],
        companyName: [shipping.companyName || '', Validators.required],
        shippingAddress: [shipping.shippingAddress || '', Validators.required],
        street: [shipping.street || '', Validators.required],
        postalCode: [shipping.postalCode || '', Validators.required],
        country: [shipping.country || '', Validators.required],
        fax: [shipping.fax || '', Validators.required],
        isSelectedShipping: [shipping.isSelectedShipping || false],
      }));
    });

    // Set selectedShippingIndex based on isSelectedShipping
    const defaultShipping = this.shippingDetails.controls.find(
      (control, i) => control.get('isSelectedShipping')?.value
    );
    if (defaultShipping) {
      this.selectedShippingIndex = this.shippingDetails.controls.indexOf(defaultShipping);
    } else {
      this.selectedShippingIndex = null;
    }

    console.log('FormArray controls:', this.shippingDetails.controls.map(control => control.value));
  }

  addShippingDetail(): void {
    this.isAdding = true;
    this.tempShippingForm.reset({
      shippingID: this.generateShortUUID(), // Set a new shippingID
    });
  }

  editShippingDetail(index: number): void {
    this.isEditing = true;
    this.editingIndex = index;
    const shipping = this.shippingDetails.at(index);
    this.tempShippingForm.patchValue(shipping.value);
  }

  saveShippingDetail(): void {
    if (this.tempShippingForm.valid) {
      const shippingData = this.tempShippingForm.value;

      if (this.isEditing && this.editingIndex !== null) {
        // Update existing shipping detail
        this.shippingDetails.at(this.editingIndex).patchValue({
          shippingID: shippingData.shippingID,
          companyName: shippingData.companyName,
          shippingAddress: shippingData.shippingAddress,
          street: shippingData.street,
          postalCode: shippingData.postalCode,
          country: shippingData.country,
          fax: shippingData.fax,
          isSelectedShipping: shippingData.isSelectedShipping,
        });
        if (shippingData.isSelectedShipping) {
          this.setDefaultShipping(shippingData.shippingID); // Use shippingID
        }
      } else {
        // Add new shipping detail
        this.shippingDetails.push(this.fb.group({
          shippingID: [shippingData.shippingID || this.generateShortUUID()],
          companyName: [shippingData.companyName, Validators.required],
          shippingAddress: [shippingData.shippingAddress, Validators.required],
          street: [shippingData.street, Validators.required],
          postalCode: [shippingData.postalCode, Validators.required],
          country: [shippingData.country, Validators.required],
          fax: [shippingData.fax, Validators.required],
          isSelectedShipping: [shippingData.isSelectedShipping],
        }));
        if (shippingData.isSelectedShipping) {
          this.setDefaultShipping(shippingData.shippingID); // Use shippingID
        }
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Successful',
        detail: this.isEditing ? 'Shipping Address Updated' : 'Shipping Address Added',
        life: 3000,
      });

      this.isAdding = false;
      this.isEditing = false;
      this.editingIndex = null;
      this.tempShippingForm.reset({
        shippingID: this.generateShortUUID(),
      });
    }
  }

  removeShippingDetail(index: number): void {
    if (this.shippingDetails.length > 1) {
      const removedShippingID = this.shippingDetails.at(index).get('shippingID')?.value;
      this.shippingDetails.removeAt(index);
      if (this.selectedShippingIndex === index) {
        this.selectedShippingIndex = null; // Reset if the default was removed
        // Optionally, select the first remaining shipping detail as default
        if (this.shippingDetails.length > 0) {
          const newDefaultShippingID = this.shippingDetails.at(0).get('shippingID')?.value;
          this.setDefaultShipping(newDefaultShippingID);
        }
      } else if (this.selectedShippingIndex !== null && this.selectedShippingIndex > index) {
        this.selectedShippingIndex--; // Adjust index if needed
      }
      this.messageService.add({
        severity: 'success',
        summary: 'Successful',
        detail: 'Shipping Address Deleted',
        life: 3000,
      });
    }
  }

  setDefaultShipping(shippingID: string): void {
    console.log('Selected shippingID:', shippingID);
    this.shippingDetails.controls.forEach((control, i) => {
      const isSelected = control.get('shippingID')?.value === shippingID;
      console.log('Control:', control.value, 'isSelected:', isSelected);
      control.get('isSelectedShipping')?.setValue(isSelected);
      if (isSelected) {
        this.selectedShippingIndex = i;
      }
    });
  }

  cancelShippingForm(): void {
    this.isAdding = false;
    this.isEditing = false;
    this.editingIndex = null;
    this.tempShippingForm.reset({
      shippingID: this.generateShortUUID(), // Reset with a new shippingID
    });
  }

  onSubmit(): void {
    if (this.customerForm.valid) {
      const customerData = this.customerForm.value;
      if (this.isEditMode) {
        this.customerUpdated.emit(customerData);
      } else {
        this.customerCreated.emit(customerData);
      }
      this.display = false;
      this.resetForm();
    }
  }

  onCancel(): void {
    this.display = false;
    this.resetForm();
  }
}